import type { ReactNode } from "react";
import { useIsMobile } from "../ai/widgets";
import ui from "../ai/ai.module.css";

export type SimpleColumn<T> = {
  key: string;
  title: string;
  numeric?: boolean;
  className?: string;
  render: (row: T) => ReactNode;
};

/**
 * C28 sin enlace de fila (tablas de ajustes C38 y de datos de una ficha) que en el celular es lista C17: el título, una
 * línea y el control al final, para que ninguna tabla se vea a 390 px (E83).
 */
export function SimpleTable<T>({
  label,
  columns,
  rows,
  rowKey,
  mobile,
  className,
  part = "tabla",
}: {
  label: string;
  columns: SimpleColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  /**
   * `oneLine`: la segunda línea en una sola línea con «…» (C17, E11), en vez de hasta dos. `href`: la fila se toca entera
   * (rowLinkArea, como Personas) y el control del final queda fuera del enlace; un enlace dentro de la línea agrandaría
   * la fila por su zona táctil (C28 · conteo que lleva a una lista, en el celular).
   */
  mobile: (row: T) => {
    title: ReactNode;
    tag?: ReactNode;
    line?: ReactNode;
    end?: ReactNode;
    oneLine?: boolean;
    href?: string;
  };
  className?: string;
  part?: string;
}) {
  const isMobile = useIsMobile();
  if (isMobile)
    return (
      <ul className={`tm-list ${ui.list}`} aria-label={label} data-part="lista">
        {rows.map((row) => {
          const view = mobile(row);
          const text = (
            <span className={ui.rowText}>
              <span className={ui.line1}>
                <strong>{view.title}</strong>
                {view.tag}
              </span>
              {view.line ? (
                <span className={view.oneLine ? `${ui.line2} ${ui.oneLine}` : ui.line2}>{view.line}</span>
              ) : null}
            </span>
          );
          return (
            <li key={rowKey(row)} className={ui.row}>
              {view.href ? (
                <a className={ui.rowLinkArea} href={view.href}>
                  {text}
                </a>
              ) : (
                text
              )}
              {view.end ? <span className={ui.rowEnd}>{view.end}</span> : null}
            </li>
          );
        })}
      </ul>
    );
  return (
    <table className={`${ui.table}${className ? ` ${className}` : ""}`} aria-label={label} data-part={part}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={
                [column.numeric ? ui.numeric : "", column.className ?? ""].filter(Boolean).join(" ") || undefined
              }
            >
              {column.title}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={rowKey(row)}>
            {columns.map((column) => (
              <td
                key={column.key}
                className={
                  [column.numeric ? ui.numeric : "", column.className ?? ""].filter(Boolean).join(" ") || undefined
                }
              >
                {column.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** C09 en una celda: interruptor con su palabra (Encendido, Concedido, Activa). */
export function CellSwitch({
  checked,
  label,
  text,
  disabled,
  onChange,
  className,
  trackClass,
  hideText,
}: {
  checked: boolean;
  label: string;
  text: string;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  className?: string;
  trackClass?: string;
  /** Solo el interruptor (la palabra ya está en la fila); el nombre accesible la conserva. */
  hideText?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      className={className}
      aria-checked={checked ? "true" : "false"}
      aria-label={`${label}: ${text}`}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className={trackClass} aria-hidden="true" />
      {hideText ? null : <span>{text}</span>}
    </button>
  );
}
